import LocalParkingIcon from '@mui/icons-material/LocalParking';
import { useTranslation } from 'react-i18next';
import { useGetOccupancyQuery } from '@/api/billing';
import StatWidgetBox from './StatWidgetBox';
import { selectActiveBuilding, selectActiveBuildingId } from '@/features/building/buildingSlice';
import { useSelector } from 'react-redux';
import TooltipStack from './partials/TooltipStack';
import TooltipContentContainer from './partials/TooltipContentContainer';

const ParkingChip = ({onClick, open}) => {
  const mainId = useSelector(selectActiveBuildingId);
  const building = useSelector(selectActiveBuilding); 
  const parkingPlacesCount = building?.parking_places ?? 0;
  const { data: occupancy, isLoading } = useGetOccupancyQuery(mainId, {
    skip: !mainId || parkingPlacesCount <= 0,
    pollingInterval: 30000 
  });
  const freeSpacesCount = occupancy?.free ?? 0;
  
  return (
    <StatWidgetBox
      icon={<LocalParkingIcon sx={{ fontSize: 16 }} />}
      value={freeSpacesCount}
      loading={isLoading}
      skeletonWidth={24}
      onClick={onClick}
      tooltipContent={open && <TooltipContent />}
    />
  );
};

const TooltipContent = () => {
  const { t } = useTranslation();
  const mainId = useSelector(selectActiveBuildingId);
  const building = useSelector(selectActiveBuilding);
  const parkingPlacesCount = building?.parking_places ?? 0;
  const { data: occupancy } = useGetOccupancyQuery(mainId, {
    skip: !mainId || parkingPlacesCount <= 0,
    pollingInterval: 30000
  });
  const occupiedCount = occupancy?.occupied ?? 0;
  const freeSpacesCount = occupancy?.free ?? 0;

  const getParkingWidgetColor = () => {
    if (!parkingPlacesCount) return 'rgb(156, 39, 176)';
    const occupancyRate = freeSpacesCount / parkingPlacesCount;
    if (occupancyRate > 0.5) return 'rgb(76, 175, 80)';
    if (occupancyRate > 0.2) return 'rgb(255, 152, 0)';
    return 'rgba(223, 15, 36, 1)';
  };

  const getParkingWidgetGradient = () => {
    if (!parkingPlacesCount)
      return 'linear-gradient(140.9deg, rgb(186, 104, 200) -14.02%, rgba(186, 104, 200, 0) 77.58%)';
    const occupancyRate = freeSpacesCount / parkingPlacesCount;
    if (occupancyRate > 0.5)
      return 'linear-gradient(140.9deg, rgb(129, 199, 132) -14.02%, rgba(129, 199, 132, 0) 77.58%)';
    if (occupancyRate > 0.2)
      return 'linear-gradient(140.9deg, rgb(255, 183, 77) -14.02%, rgba(255, 183, 77, 0) 77.58%)';
    return 'linear-gradient(140.9deg, rgb(186, 104, 200) -14.02%, rgba(186, 104, 200, 0) 77.58%)';
  };

  const widgetColor = getParkingWidgetColor();

  return (
    <TooltipContentContainer
      backgroundColor={widgetColor}
      backgroundGradient={getParkingWidgetGradient()}
      icon={<LocalParkingIcon sx={{ fontSize: 14, color: 'white' }} />}
      iconBackground={'rgba(255, 255, 255, 0.2)'}
      iconBorder={'1px solid rgba(255, 255, 255, 0.4)'}
      title={t('available_seats')}
    >
      <TooltipStack 
        items={[
          { label: t('total'), value: parkingPlacesCount },
          { label: t('free'), value: freeSpacesCount },
          { label: t('occupied'), value: occupiedCount }
        ]}
        colorTheme="light"
        backgroundColor="rgb(255, 255, 255)"
        valueTransformFn={(value) => `${value || '0'}`}
      />
    </TooltipContentContainer>
  );
}
export default ParkingChip;
